import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { submitCustomerForm, submitCraftCustomerForm, submitBackupLog, trackFacebookPixel, trackGoogleAnalytics } from '../../utils/api';
import { getCurrentDateTime } from '../../utils/formatting';
import { showDuplicateModal } from '../../utils/duplicateModal';

export const useFormSubmit = (urlCode, urlCodeSettingId, endpoint = null) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const MIN_DISPLAY_MS = 300;

  const handleSubmit = async (formData) => {
    const startTime = Date.now();
    // flushSync: 오버레이가 API 호출 전에 반드시 렌더링되도록 보장
    flushSync(() => {
      setIsSubmitting(true);
      setError(null);
    });

    // 전화번호 포맷팅
    const { phoneNumber } = formData;
    const formattedPhone = `${phoneNumber.first}-${phoneNumber.second}-${phoneNumber.third}`;

    const submitData = {
      name: formData.name,
      phoneNumber: formattedPhone,
      urlCode,
      urlCodeSettingId,
      date: getCurrentDateTime(),
      ...(formData.selectedOption && { selectedOption: formData.selectedOption }),
      ...(formData.selectedOption2 && { selectedOption2: formData.selectedOption2 }),
      ...(formData.kakaotalk && { kakaotalk: formData.kakaotalk }),
      ...(endpoint && { apiUrl: endpoint })
    };

    try {
      // 1단계: 메인 API 호출
      let response;
      if (endpoint) {
        response = await submitCraftCustomerForm(submitData);
      } else {
        response = await submitCustomerForm(submitData);
      }

      // 제출 성공 시 추적 이벤트 발생
      if (response && response.status >= 200 && response.status < 300) {
        trackGoogleAnalytics({ phoneNumber: formattedPhone });
        trackFacebookPixel();
      }

      console.log('폼 제출 성공');
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_MS) await new Promise(r => setTimeout(r, MIN_DISPLAY_MS - elapsed));
      navigate('/thanks');
    } catch (mainError) {
      // 30일 중복 차단
      if (mainError.response?.status === 409) {
        const message = mainError.response?.data?.message || '이미 상담 접수된 번호입니다.';
        console.log(`[30일 중복 차단] ${message}`);
        showDuplicateModal(message);
        setIsSubmitting(false);
        return;
      }

      console.error('메인 API 실패:', mainError);

      try {
        // 2단계: 백업 API 호출
        await submitBackupLog({
          name: submitData.name,
          phoneNumber: submitData.phoneNumber,
          date: submitData.date,
          urlCode: submitData.urlCode
        });

        console.log('백업 API 성공');
        const elapsed = Date.now() - startTime;
        if (elapsed < MIN_DISPLAY_MS) await new Promise(r => setTimeout(r, MIN_DISPLAY_MS - elapsed));
        navigate('/thanks');
      } catch (backupError) {
        console.error('백업 API 실패:', backupError);
        setError(new Error('폼 제출에 실패했습니다. 잠시 후 다시 시도해주세요.'));
        alert('신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return { handleSubmit, isSubmitting, error };
};

export default useFormSubmit;
